import { SelectInput } from '@/components/form/fields/select/SelectInput';
import { SidebarLink } from '@/components/sidebar/SidebarLink';
import { SidebarMainSection } from '@/components/sidebar/SidebarMainSection';
import SiPlus from '@/icons/SiPlus';
import { useCustomerRoutePrefix } from '@/services/customer/useCustomerRoutePrefix';
import { useDraftPurchaseOrders, useInProgressPurchaseOrders } from '@/services/purchaseOrders/usePurchaseOrders';
import { FC } from 'react';
import { useNavigate, useParams } from 'react-router';
import { CategoryLinks } from './CategoryLinks';

const CustomerPurchaseOrderLinks: FC<{ selectedOrderId: string; baseUrl: string }> = ({ selectedOrderId, baseUrl }) => {
  const orderBaseUrl = `${baseUrl}/${selectedOrderId}/poCatalogue`;
  return (
    <>
      <CategoryLinks baseId="po" baseUrl={orderBaseUrl} />
      <SidebarLink to={`${orderBaseUrl}/selectedProducts`}>My Orders / Quotes</SidebarLink>
      <SidebarLink to={`${orderBaseUrl}/standardisedProducts`}>Standardised Products</SidebarLink>
      <SidebarLink to={`${orderBaseUrl}/deliveryDetails`}>Delivery Details</SidebarLink>
      <SidebarLink to={`${orderBaseUrl}/confirm`}>Order Summary</SidebarLink>
    </>
  );
};

export const CustomerLinks: FC<{ customerId: string; partnerId: string }> = ({ customerId, partnerId }) => {
  const prefix = useCustomerRoutePrefix(partnerId, customerId);
  const cataloguePrefix = `${prefix}/catalogue`;
  const standardisePrefix = `${prefix}/standardise`;
  const bundlePrefix = `${prefix}/bundles`;

  const { orderId: selectedPurchaseOrder } = useParams<{
    orderId: string;
  }>();

  const draftOrders = useDraftPurchaseOrders(partnerId, customerId);
  const inProgressOrders = useInProgressPurchaseOrders(partnerId, customerId);

  const orders = [...draftOrders, ...inProgressOrders];
  const options = [
    {
      id: 'create',
      name: 'Create'
    },
    {
      id: 'orders',
      name: 'My Orders / Quotes'
    }
  ];

  const navigate = useNavigate();

  const showPoLinks = !!orders.find(o => o.id === selectedPurchaseOrder) || selectedPurchaseOrder === 'create';

  return (
    <>
      <CategoryLinks
        baseId="catalogue"
        baseUrl={`${cataloguePrefix}`}
        mainSection
        catalogueTitleOverride="Customer Catalogue"
      />
      <SidebarMainSection
        sectionUrlIdentifier="standardise"
        title={'Standardised Products'}
        to={`${standardisePrefix}/selectedProducts`}
      />
      <SidebarMainSection sectionUrlIdentifier="bundles" title={'Bundles'} to={`${bundlePrefix}`} collapsible>
        <SidebarLink to={`${bundlePrefix}`}>My Bundles</SidebarLink>
        <SidebarLink to={`${bundlePrefix}/create`}>
          <div className="flex space-x-1">
            <SiPlus className="text-xl" />
            <span>Add new bundle</span>
          </div>
        </SidebarLink>
      </SidebarMainSection>
      <SidebarMainSection
        sectionUrlIdentifier="purchaseOrders"
        title={'Orders / Quotes'}
        to={`${prefix}/purchaseOrders`}
        collapsible={options.length > 1}
      >
        {options.length > 1 && (
          <SelectInput
            id={'selectedPurchaseOrder'}
            value={options.find(o => o.id === selectedPurchaseOrder) || null}
            placeholder="Actions"
            onChange={option => {
              if (!option) {
                return;
              }

              if (option.id === 'orders') {
                navigate(`/msps/${partnerId}/companies/${customerId}/purchaseOrders`);
              } else {
                navigate(`/msps/${partnerId}/companies/${customerId}/purchaseOrders/${option.id}/poCatalogue`);
              }
            }}
            options={options}
            className="mb-2"
          />
        )}
        {showPoLinks && selectedPurchaseOrder && (
          <CustomerPurchaseOrderLinks
            selectedOrderId={selectedPurchaseOrder}
            baseUrl={`/msps/${partnerId}/companies/${customerId}/purchaseOrders`}
          />
        )}
      </SidebarMainSection>

      <SidebarMainSection
        sectionUrlIdentifier="management"
        title={'Management'}
        to={`${prefix}/management/stockProvision`}
        collapsible={true}
      >
        <SidebarLink to={`${prefix}/management/stockProvision`}>Deploy Inventory</SidebarLink>
        <SidebarLink to={`${prefix}/management/warrantyReplacement`}>Warranty Replacement</SidebarLink>
        <SidebarLink to={`${prefix}/management/leavers`}>Leavers</SidebarLink>
        <SidebarLink to={`${prefix}/management/invoices`}>Invoices</SidebarLink>
        <SidebarLink to={`${prefix}/management/stockReporting`}>Stock Reporting</SidebarLink>
      </SidebarMainSection>
      <SidebarMainSection
        sectionUrlIdentifier="advanced"
        title={'Advanced Features'}
        to={`${prefix}/advanced/financeModels`}
        collapsible={true}
      >
        <SidebarLink to={`${prefix}/advanced/financeModels`}>Finance / Purchase Models</SidebarLink>
        <SidebarLink to={`${prefix}/advanced/lifecyclePolicies`}>Lifecycle Policies</SidebarLink>
        <SidebarLink to={`${prefix}/advanced/stockCalculator`}>Bundle Stock Parameters</SidebarLink>
        <SidebarLink to={`${prefix}/advanced/bundleStockLevels`}>Bundle Stock Levels</SidebarLink>
        <SidebarLink to={`${prefix}/advanced/generalStockLevels`}>Additional Stock Levels</SidebarLink>
        <SidebarLink to={`${prefix}/advanced/summaryStockLevels`}>Stock Summary</SidebarLink>
      </SidebarMainSection>
    </>
  );
};
